<template>
  <div
    v-if="visible"
    class="global-chat"
    :class="{ 'global-chat--left': dodgeToasts }">
    <!-- Collapsed bubble -->
    <div v-if="!panelOpen" class="chat-fab-wrap">
      <button
        class="chat-fab"
        :class="{ 'chat-fab--unread': totalUnread > 0 }"
        title="Chats de operadores"
        @click="openPanel">
        <svg viewBox="0 0 24 24" class="chat-fab-icon" fill="currentColor">
          <path d="M12 3C6.48 3 2 6.94 2 11.8c0 2.72 1.4 5.14 3.6 6.75V22l3.4-1.87c.95.26 1.96.4 3 .4 5.52 0 10-3.94 10-8.8S17.52 3 12 3z" />
        </svg>
        <span v-if="totalUnread > 0" class="chat-fab-badge">{{ badgeText }}</span>
      </button>
    </div>

    <!-- Expanded side panel -->
    <div v-else class="chat-panel">
      <div class="chat-panel-header">
        <button
          v-if="activeThread"
          class="chat-panel-btn chat-panel-back"
          title="Regresar a la lista"
          @click="backToList">‹</button>
        <div class="chat-panel-title">
          <template v-if="activeThread">
            <div class="chat-panel-title-main">OT {{ threadLabel(activeThread) }}</div>
            <div class="chat-panel-title-sub">{{ activeThread.operator_name }}</div>
          </template>
          <template v-else>
            <div class="chat-panel-title-main">Chats de operadores</div>
            <div class="chat-panel-title-sub">
              {{ totalUnread > 0 ? `${totalUnread} sin leer` : 'Todo leído' }}
            </div>
          </template>
        </div>
        <button
          class="chat-panel-btn chat-panel-minimize"
          title="Minimizar"
          @click="minimize">—</button>
      </div>

      <!-- Thread list -->
      <div v-if="!activeThread" class="chat-thread-list">
        <div v-if="threads.length === 0" class="chat-empty">
          No hay chats activos por el momento.
        </div>
        <button
          v-for="thread in threads"
          :key="thread.note_ident"
          class="chat-item"
          :class="{ 'chat-item--unread': thread.unread_count > 0 }"
          @click="openThread(thread)">
          <div class="chat-item-text">
            <div class="chat-item-title">OT {{ threadLabel(thread) }}</div>
            <div class="chat-item-operator">{{ thread.operator_name }}</div>
            <div v-if="thread.last_message_preview" class="chat-item-preview">
              {{ thread.last_message_preview }}
            </div>
          </div>
          <div class="chat-item-meta">
            <div class="chat-item-time">{{ formatTime(thread.last_message_at) }}</div>
            <span v-if="thread.unread_count > 0" class="chat-item-badge">
              {{ thread.unread_count }}
            </span>
          </div>
        </button>
      </div>

      <!-- Open conversation (reuses the per-note chat component) -->
      <div v-else class="chat-thread-view">
        <NoteChat
          :key="activeThread.note_ident"
          :note-ident="activeThread.note_ident"
          @read="onThreadRead(activeThread)" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import NoteChat from './NoteChat.vue';
import { useDashboardWS } from '../composables/useDashboardWS';

const apiDirectory = process.env.API_DIRECTORY;

export default {
  name: 'GlobalChatBubble',
  components: { NoteChat },
  setup() {
    const {
      status: chatWsStatus,
      onMessage: onChatWsMessage,
      connect: connectChatWs,
      disconnect: disconnectChatWs,
    } = useDashboardWS('chat', { manual: true });
    return { chatWsStatus, onChatWsMessage, connectChatWs, disconnectChatWs };
  },
  data() {
    return {
      threads: [],
      totalUnread: 0,
      panelOpen: false,
      activeThread: null,
      // null = unknown (hidden until the local permission check resolves).
      hasPerm: null,
      refetchTimer: null,
      // note_ident -> last_message_id, to detect brand-new incoming messages.
      knownLastIds: {},
      initialized: false,
      lastSoundAt: 0,
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    },
    visible() {
      return this.isAuthenticated && this.hasPerm === true;
    },
    badgeText() {
      return this.totalUnread > 99 ? '99+' : String(this.totalUnread);
    },
    // Insurer toasts occupy the bottom-right corner; yield it while they show.
    dodgeToasts() {
      return this.$store.state.insurerToastCount > 0;
    },
  },
  watch: {
    isAuthenticated(authenticated) {
      if (authenticated) {
        this.startLiveFeed();
      } else {
        this.stopLiveFeed();
        this.resetState();
      }
    },
    chatWsStatus(value) {
      // Catch up whenever the socket (re)opens: pings may have been missed
      // while offline, and with no fallback poll this is the only recovery
      // path (it also retries a failed initial fetch).
      if (value === 'open') {
        this.scheduleRefetch();
      }
    },
  },
  created() {
    this.onChatWsMessage((msg) => {
      if (msg && msg.topic === 'note_chat_message') {
        this.scheduleRefetch();
      }
    });
    if (this.isAuthenticated) {
      this.startLiveFeed();
    }
  },
  beforeUnmount() {
    this.stopLiveFeed();
  },
  methods: {
    // Pure push: one fetch for the initial state, then websocket pings (and
    // reconnects) drive every refresh. No polling.
    startLiveFeed() {
      this.stopLiveFeed();
      this.fetchThreads();
      this.connectChatWs();
    },
    stopLiveFeed() {
      if (this.refetchTimer) {
        clearTimeout(this.refetchTimer);
        this.refetchTimer = null;
      }
      this.disconnectChatWs();
    },
    scheduleRefetch() {
      // Coalesce ping bursts, and add jitter so one message doesn't make
      // every open tab hit the API at the same instant.
      if (this.refetchTimer) return;
      this.refetchTimer = setTimeout(() => {
        this.refetchTimer = null;
        this.fetchThreads();
      }, 150 + Math.random() * 850);
    },
    resetState() {
      this.threads = [];
      this.totalUnread = 0;
      this.panelOpen = false;
      this.activeThread = null;
      this.hasPerm = null;
      this.knownLastIds = {};
      this.initialized = false;
    },
    authHeaders() {
      return { Authorization: 'Token ' + localStorage.getItem('authToken') };
    },
    // true = has operations.view_chat_bubble, false = confirmed missing,
    // null = permissions not loaded into localStorage yet (right after login).
    bubblePermissionState() {
      const stored = localStorage.getItem('userPermissions');
      if (stored === null) return null;
      let permissions = [];
      try {
        permissions = JSON.parse(stored) || [];
      } catch (error) {
        permissions = [];
      }
      return permissions.includes('operations.view_chat_bubble');
    },
    async fetchThreads() {
      if (!this.isAuthenticated) return;
      const permission = this.bubblePermissionState();
      if (permission === null) {
        // Permissions load right after login; retry shortly (without hitting
        // the server) instead of waiting for the slow fallback poll.
        setTimeout(() => {
          if (this.isAuthenticated && this.hasPerm === null) this.fetchThreads();
        }, 2000);
        return;
      }
      if (permission === false) {
        this.hasPerm = false;
        this.stopLiveFeed();
        return;
      }
      // Local permission says yes: show the bubble right away (an empty
      // thread list is fine) instead of waiting for the first successful
      // fetch. A server 403 below is still authoritative to hide it.
      this.hasPerm = true;
      try {
        const response = await axios.get(
          `${apiDirectory}/notes/chat/active-threads/`,
          { headers: this.authHeaders() }
        );
        this.applyThreads(response.data);
      } catch (error) {
        if (error?.response?.status === 403) {
          // The server is authoritative: hide the bubble and disconnect.
          this.hasPerm = false;
          this.stopLiveFeed();
        }
        // Network hiccups: keep the last known state; the next ping or
        // socket reconnect will refresh it.
      }
    },
    applyThreads(data) {
      const threads = data.threads || [];
      let incomingMessage = false;

      if (this.initialized) {
        for (const thread of threads) {
          // Ring only for messages that are unread *for this user*: if the
          // thread was already read elsewhere (another tab/window), a sound
          // with no badge or glow to back it up just confuses people.
          if (!thread.last_from_operator || !thread.last_message_id) continue;
          if (!thread.unread_count) continue;
          const previousId = this.knownLastIds[thread.note_ident];
          const isNew = previousId === undefined
            || thread.last_message_id > previousId;
          const viewingThread =
            this.panelOpen &&
            this.activeThread &&
            this.activeThread.note_ident === thread.note_ident;
          if (isNew && !viewingThread) {
            incomingMessage = true;
            break;
          }
        }
      }

      const knownLastIds = {};
      for (const thread of threads) {
        knownLastIds[thread.note_ident] = thread.last_message_id;
      }
      this.knownLastIds = knownLastIds;
      this.threads = threads;
      this.totalUnread = data.total_unread || 0;
      this.initialized = true;

      if (incomingMessage) {
        this.playNotificationSound();
      }
    },
    openPanel() {
      this.panelOpen = true;
      this.fetchThreads();
    },
    minimize() {
      this.panelOpen = false;
    },
    openThread(thread) {
      this.activeThread = thread;
    },
    backToList() {
      this.activeThread = null;
      this.fetchThreads();
    },
    onThreadRead(thread) {
      // NoteChat marked the thread as read server-side; mirror it locally so
      // the badge clears without waiting for the next poll.
      const item = this.threads.find(
        (candidate) => candidate.note_ident === thread.note_ident
      );
      if (item && item.unread_count !== 0) {
        item.unread_count = 0;
        this.totalUnread = this.threads.reduce(
          (sum, candidate) => sum + (candidate.unread_count || 0),
          0
        );
      }
    },
    threadLabel(thread) {
      return thread.note_number || thread.note_ident;
    },
    formatTime(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
    playNotificationSound() {
      const now = Date.now();
      // Avoid stacking sounds when several messages land close together.
      if (now - this.lastSoundAt < 2500) return;
      this.lastSoundAt = now;

      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const context = new AudioCtx();
        const gain = context.createGain();
        gain.gain.setValueAtTime(0.22, context.currentTime);
        gain.connect(context.destination);

        // Two quick ascending tones: a short messenger-style "pop".
        [[880, 0], [1174.66, 0.14]].forEach(([frequency, start]) => {
          const oscillator = context.createOscillator();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(frequency, context.currentTime + start);
          oscillator.connect(gain);
          oscillator.start(context.currentTime + start);
          oscillator.stop(context.currentTime + start + 0.18);
        });

        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.5);
        setTimeout(() => {
          if (context.state !== 'closed') {
            context.close().catch(() => {});
          }
        }, 700);
      } catch (error) {
        // Sound is best-effort; never break the UI over audio issues.
      }
    },
  },
};
</script>

<style scoped>
.global-chat {
  position: fixed;
  bottom: 18px;
  right: 16px;
  z-index: 9500;
  font-family: 'Poppins', sans-serif;
  text-align: left;
}

/* While insurer toasts occupy the bottom-right, step over to the left. */
.global-chat--left {
  right: auto;
  left: 16px;
}

/* ---- Collapsed bubble ---- */
.chat-fab-wrap {
  position: relative;
}

.chat-fab {
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  background: #d5171d;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
  transition: transform 0.12s ease, background 0.12s ease;
}

.chat-fab:hover {
  background: #b11217;
  transform: scale(1.06);
}

/* Brand-yellow glow while there are unread messages. */
.chat-fab--unread {
  animation: chatYellowGlow 1.5s ease-in-out infinite;
}

@keyframes chatYellowGlow {
  0%,
  100% {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28), 0 0 0 0 rgba(255, 196, 52, 0.85);
  }
  50% {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28), 0 0 16px 5px rgba(255, 196, 52, 0.85);
  }
}

.chat-fab-icon {
  width: 26px;
  height: 26px;
}

.chat-fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 21px;
  height: 21px;
  padding: 0 5px;
  border-radius: 11px;
  background: #ffc434;
  color: #1a1a1a;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-sizing: border-box;
}

/* ---- Expanded panel ---- */
.chat-panel {
  width: 380px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 28px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-panel-header {
  background: #d5171d;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  flex-shrink: 0;
}

.chat-panel-title {
  flex: 1;
  min-width: 0;
}

.chat-panel-title-main {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-panel-title-sub {
  font-size: 11px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-panel-btn {
  background: rgba(255, 255, 255, 0.16);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chat-panel-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-panel-back {
  font-size: 22px;
  padding-bottom: 3px;
}

/* ---- Thread list ---- */
.chat-thread-list {
  overflow-y: auto;
  min-height: 120px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
}

.chat-empty {
  color: #999;
  font-size: 13px;
  text-align: center;
  padding: 36px 16px;
}

.chat-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: white;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  border-left: 4px solid transparent;
  padding: 10px 12px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
}

.chat-item:hover {
  background: #fafafa;
}

.chat-item--unread {
  border-left-color: #d5171d;
  background: rgba(213, 23, 29, 0.07);
  animation: chatItemYellowGlow 1.5s ease-in-out infinite;
}

.chat-item--unread:hover {
  background: rgba(213, 23, 29, 0.12);
}

/* Inset so the glow isn't clipped by the scrolling list container. */
@keyframes chatItemYellowGlow {
  0%,
  100% {
    box-shadow: inset 0 0 0 0 rgba(255, 196, 52, 0);
  }
  50% {
    box-shadow: inset 0 0 14px 2px rgba(255, 196, 52, 0.75);
  }
}

.chat-item-text {
  flex: 1;
  min-width: 0;
}

.chat-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
}

.chat-item--unread .chat-item-title {
  color: #d5171d;
}

.chat-item-operator {
  font-size: 12px;
  color: #555;
}

.chat-item--unread .chat-item-operator {
  color: #b11217;
  font-weight: 500;
}

.chat-item-preview {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-item-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.chat-item-time {
  font-size: 10px;
  color: #aaa;
  white-space: nowrap;
}

.chat-item-badge {
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 10px;
  background: #d5171d;
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

/* ---- Embedded conversation ---- */
.chat-thread-view {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chat-thread-view :deep(.note-chat) {
  padding: 0;
}

.chat-thread-view :deep(.chat-card) {
  border: none;
  border-radius: 0;
  max-width: none;
}

/* The panel header already shows the OT + operator. */
.chat-thread-view :deep(.chat-header) {
  display: none;
}

.chat-thread-view :deep(.chat-messages) {
  height: 380px;
  max-height: calc(100vh - 190px);
}
</style>
